// ============================================
// AlpenScreen - Super Admin User Management Component
// File: frontend/src/components/SuperAdminUsers.js
// Purpose: Enhanced user management with edit, filter, search, bulk actions, ALL FIELDS
// ============================================

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Users,
  Edit,
  Save,
  X,
  Trash2,
  Search,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Mail,
  Key,
  CheckSquare,
  Square,
  UserCog
} from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const SuperAdminUsers = () => {
  const { t } = useTranslation();
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [orgFilter, setOrgFilter] = useState('all');  // ✅ NEU
  const [editingUser, setEditingUser] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);  // ✅ NEU
  const [showBulkActions, setShowBulkActions] = useState(false);  // ✅ NEU
  const [editForm, setEditForm] = useState({
    email: '',
    name: '',
    role: '',
    phone: '',
    company: '',
    street: '',
    city: '',
    postal_code: '',
    country: '',
    language: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchTerm, roleFilter, orgFilter]);  // ✅ orgFilter hinzugefügt

  const loadUsers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to load users');

      const data = await response.json();
      setUsers(data);
    } catch (error) {
      console.error('Error loading users:', error);
      alert('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = [...users];

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(user => 
        user.email?.toLowerCase().includes(term) ||
        user.name?.toLowerCase().includes(term) ||
        user.organization_name?.toLowerCase().includes(term)
      );
    }

    // Role filter
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // ✅ NEU: Organization filter
    if (orgFilter !== 'all') {
      filtered = filtered.filter(user => user.organization_id === parseInt(orgFilter));
    }

    setFilteredUsers(filtered);
  };

  // ✅ NEU: Get unique organizations for filter
  const getOrganizations = () => {
    const orgs = new Map();
    users.forEach(user => {
      if (user.organization_id && user.organization_name) {
        orgs.set(user.organization_id, user.organization_name);
      }
    });
    return Array.from(orgs.entries()).map(([id, name]) => ({ id, name }));
  };

  // ✅ NEU: Toggle user selection
  const toggleUserSelection = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  // ✅ NEU: Select all users
  const toggleSelectAll = () => {
    if (selectedUsers.length === filteredUsers.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredUsers.map(u => u.id));
    }
  };

  // ✅ NEU: Bulk delete
  const handleBulkDelete = async () => {
    if (selectedUsers.length === 0) {
      alert('No users selected');
      return;
    }

    const confirmText = `Delete ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}? This action cannot be undone!`;
    if (!window.confirm(confirmText)) return;

    try {
      const token = localStorage.getItem('token');
      const promises = selectedUsers.map(userId =>
        fetch(`${API_URL}/superadmin/users/${userId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` }
        })
      );

      await Promise.all(promises);
      alert(`${selectedUsers.length} user(s) deleted successfully!`);
      setSelectedUsers([]);
      setShowBulkActions(false);
      loadUsers();
    } catch (error) {
      console.error('Error bulk deleting users:', error);
      alert('Failed to delete some users');
    }
  };

  // ✅ NEU: Bulk role change
  const handleBulkRoleChange = async (newRole) => {
    if (selectedUsers.length === 0) {
      alert('No users selected');
      return;
    }

    const confirmText = `Change role to "${newRole}" for ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}?`;
    if (!window.confirm(confirmText)) return;

    try {
      const token = localStorage.getItem('token');
      const promises = selectedUsers.map(userId =>
        fetch(`${API_URL}/superadmin/users/${userId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ role: newRole })
        })
      );

      await Promise.all(promises);
      alert(`${selectedUsers.length} user(s) updated successfully!`);
      setSelectedUsers([]);
      setShowBulkActions(false);
      loadUsers();
    } catch (error) {
      console.error('Error bulk updating users:', error);
      alert('Failed to update some users');
    }
  };

  // ✅ NEU: Send password reset email
  const handlePasswordReset = async (userEmail) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/users/password-reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userEmail })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send password reset email');
      }

      alert(`Password reset email sent to ${userEmail}!`);
    } catch (error) {
      console.error('Error sending password reset:', error);
      alert(error.message);
    }
  };

  // ✅ NEU: Resend verification email
  const handleResendVerification = async (userEmail) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/users/resend-verification`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userEmail })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to send verification email');
      }

      alert(`Verification email sent to ${userEmail}!`);
    } catch (error) {
      console.error('Error sending verification email:', error);
      alert(error.message);
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user.id);
    setEditForm({
      email: user.email || '',
      name: user.name || '',
      role: user.role || '',
      phone: user.phone || '',
      company: user.company || '',
      street: user.street || '',
      city: user.city || '',
      postal_code: user.postal_code || '',
      country: user.country || '',
      language: user.language || 'de'
    });
  };

  const handleSave = async (userId, formData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/users/${userId}`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to update user');
      }

      alert('User updated successfully!');
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      alert(error.message);
    }
  };

  const handleDelete = async (userId, userEmail) => {
    if (!window.confirm(`Delete user ${userEmail}? This action cannot be undone!`)) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/superadmin/users/${userId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete user');
      }

      alert('User deleted successfully!');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      alert(error.message);
    }
  };

  // ============================================
  // EDIT MODAL COMPONENT
  // ============================================
  const EditUserModal = ({ user, onClose, onSave }) => {
    const [form, setForm] = useState(editForm);

    useEffect(() => {
      setForm(editForm);
    }, []);

    const handleSubmit = (e) => {
      e.preventDefault();
      onSave(user.id, form);
    };

    return (
      <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4 overflow-y-auto">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full my-8">
          <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              Edit User: {user.name}
            </h3>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
            >
              <X size={24} />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
            {/* Basic Info */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Name *
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Role *
                </label>
                <select
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  required
                >
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                  <option value="super_admin">Super Admin</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Language
                </label>
                <select
                  value={form.language}
                  onChange={(e) => setForm({ ...form, language: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                </select>
              </div>
            </div>

            {/* Contact Info */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Contact Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="+41 79 123 45 67"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    value={form.company}
                    onChange={(e) => setForm({ ...form, company: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Company Name"
                  />
                </div>
              </div>
            </div>

            {/* Address Info */}
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                Address
              </h4>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Street
                  </label>
                  <input
                    type="text"
                    value={form.street}
                    onChange={(e) => setForm({ ...form, street: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                    placeholder="Bahnhofstrasse 1"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Postal Code
                    </label>
                    <input
                      type="text"
                      value={form.postal_code}
                      onChange={(e) => setForm({ ...form, postal_code: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="8000"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      City
                    </label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Zürich"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Country
                    </label>
                    <input
                      type="text"
                      value={form.country}
                      onChange={(e) => setForm({ ...form, country: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                      placeholder="Switzerland"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <button
                type="submit"
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 font-medium"
              >
                <Save size={18} />
                Save Changes
              </button>
              <button
                type="button"
                onClick={onClose}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg flex items-center justify-center gap-2 font-medium"
              >
                <X size={18} />
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="inline-block w-8 h-8 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 md:space-y-6 px-2 md:px-0">
      {/* Header with Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 shadow-md border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">Total Users</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">{users.length}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 shadow-md border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">Admins</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {users.filter(u => u.role === 'admin').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 shadow-md border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">Regular Users</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {users.filter(u => u.role === 'user').length}
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-lg p-3 md:p-4 shadow-md border border-gray-200 dark:border-gray-700">
          <div className="text-sm text-gray-600 dark:text-gray-400">Verified</div>
          <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            {users.filter(u => u.email_verified).length}
          </div>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-4 shadow-md border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row gap-3">
          {/* Search */}
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search by email, name, or organization..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Role Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Roles</option>
              <option value="super_admin">Super Admin</option>
              <option value="admin">Admin</option>
              <option value="user">User</option>
            </select>
          </div>

          {/* ✅ NEU: Organization Filter */}
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <select
              value={orgFilter}
              onChange={(e) => setOrgFilter(e.target.value)}
              className="pl-10 pr-8 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Organizations</option>
              {getOrganizations().map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        </div>

        <div className="mt-2 flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing {filteredUsers.length} of {users.length} users
            {selectedUsers.length > 0 && (
              <span className="ml-2 text-blue-600 dark:text-blue-400 font-medium">
                ({selectedUsers.length} selected)
              </span>
            )}
          </div>

          {/* ✅ NEU: Bulk Actions Toggle */}
          {selectedUsers.length > 0 && (
            <button
              onClick={() => setShowBulkActions(!showBulkActions)}
              className="text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-lg flex items-center gap-2"
            >
              <UserCog size={16} />
              Bulk Actions
            </button>
          )}
        </div>

        {/* ✅ NEU: Bulk Actions Panel */}
        {showBulkActions && selectedUsers.length > 0 && (
          <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleBulkRoleChange('user')}
                className="px-3 py-1 bg-gray-600 hover:bg-gray-700 text-white text-sm rounded-lg"
              >
                Set as User
              </button>
              <button
                onClick={() => handleBulkRoleChange('admin')}
                className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg"
              >
                Set as Admin
              </button>
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg flex items-center gap-1"
              >
                <Trash2 size={14} />
                Delete Selected
              </button>
              <button
                onClick={() => {
                  setSelectedUsers([]);
                  setShowBulkActions(false);
                }}
                className="px-3 py-1 bg-gray-400 hover:bg-gray-500 text-white text-sm rounded-lg"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Desktop Table View */}
      <div className="hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900">
              <tr>
                {/* ✅ NEU: Select All Checkbox */}
                <th className="px-6 py-3 text-left">
                  <button
                    onClick={toggleSelectAll}
                    className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                  >
                    {selectedUsers.length === filteredUsers.length && filteredUsers.length > 0 ? (
                      <CheckSquare size={20} />
                    ) : (
                      <Square size={20} />
                    )}
                  </button>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  User
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Organization
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Role
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  {/* ✅ NEU: Individual Checkbox */}
                  <td className="px-6 py-4">
                    <button
                      onClick={() => toggleUserSelection(user.id)}
                      className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      {selectedUsers.includes(user.id) ? (
                        <CheckSquare size={20} className="text-blue-600" />
                      ) : (
                        <Square size={20} />
                      )}
                    </button>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {user.name}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {user.email}
                      </div>
                      {user.phone && (
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          📞 {user.phone}
                        </div>
                      )}
                      {user.company && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          🏢 {user.company}
                        </div>
                      )}
                      {(user.city || user.country) && (
                        <div className="text-xs text-gray-500 dark:text-gray-400">
                          📍 {user.city}{user.city && user.country ? ', ' : ''}{user.country}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {user.organization_name}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      {user.subscription_tier} • {user.subscription_status}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                      user.role === 'super_admin' ? 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200' :
                      user.role === 'admin' ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200' :
                      'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                    }`}>
                      {user.role}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.email_verified ? (
                      <CheckCircle className="w-5 h-5 text-green-500" title="Verified" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500" title="Not Verified" />
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEdit(user)}
                        className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                        title="Edit"
                      >
                        <Edit size={18} />
                      </button>
                      {/* ✅ NEU: Password Reset Button */}
                      <button
                        onClick={() => handlePasswordReset(user.email)}
                        className="p-1 text-orange-600 hover:text-orange-900 dark:text-orange-400 dark:hover:text-orange-300 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded transition-colors"
                        title="Send Password Reset"
                      >
                        <Key size={18} />
                      </button>
                      {/* ✅ NEU: Resend Verification Button (only if not verified) */}
                      {!user.email_verified && (
                        <button
                          onClick={() => handleResendVerification(user.email)}
                          className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                          title="Resend Verification Email"
                        >
                          <Mail size={18} />
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(user.id, user.email)}
                        className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {filteredUsers.map((user) => (
          <div 
            key={user.id} 
            className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
          >
            <div 
              className="px-4 py-3 bg-gray-50 dark:bg-gray-700 flex justify-between items-center cursor-pointer"
              onClick={() => setExpandedUser(expandedUser === user.id ? null : user.id)}
            >
              <div className="flex items-center gap-3 flex-1">
                {/* ✅ NEU: Mobile Checkbox */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleUserSelection(user.id);
                  }}
                  className="text-gray-500"
                >
                  {selectedUsers.includes(user.id) ? (
                    <CheckSquare size={20} className="text-blue-600" />
                  ) : (
                    <Square size={20} />
                  )}
                </button>
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 dark:text-gray-100">
                    {user.name}
                  </h3>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {user.email}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                  user.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                  user.role === 'admin' ? 'bg-blue-100 text-blue-800' :
                  'bg-gray-100 text-gray-800'
                }`}>
                  {user.role}
                </span>
                {expandedUser === user.id ? (
                  <ChevronUp size={20} className="text-gray-500" />
                ) : (
                  <ChevronDown size={20} className="text-gray-500" />
                )}
              </div>
            </div>

            {expandedUser === user.id && (
              <div className="px-4 py-3 space-y-3">
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Organization</label>
                  <div className="text-sm text-gray-900 dark:text-gray-100">{user.organization_name}</div>
                </div>
                
                {/* Phone */}
                {user.phone && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Phone</label>
                    <div className="text-sm text-gray-900 dark:text-gray-100">{user.phone}</div>
                  </div>
                )}
                
                {/* Company */}
                {user.company && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Company</label>
                    <div className="text-sm text-gray-900 dark:text-gray-100">{user.company}</div>
                  </div>
                )}
                
                {/* Address */}
                {(user.street || user.city || user.postal_code || user.country) && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Address</label>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {user.street && <div>{user.street}</div>}
                      {(user.postal_code || user.city) && (
                        <div>{user.postal_code} {user.city}</div>
                      )}
                      {user.country && <div>{user.country}</div>}
                    </div>
                  </div>
                )}
                
                {/* Language */}
                {user.language && (
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400">Language</label>
                    <div className="text-sm text-gray-900 dark:text-gray-100">
                      {user.language.toUpperCase()}
                    </div>
                  </div>
                )}
                
                <div>
                  <label className="text-xs text-gray-500 dark:text-gray-400">Status</label>
                  <div className="flex items-center gap-2">
                    {user.email_verified ? (
                      <>
                        <CheckCircle className="w-4 h-4 text-green-500" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">Verified</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-4 h-4 text-red-500" />
                        <span className="text-sm text-gray-900 dark:text-gray-100">Not Verified</span>
                      </>
                    )}
                  </div>
                </div>
                
                <div className="pt-2 border-t border-gray-200 dark:border-gray-700 space-y-2">
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(user)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <Edit size={16} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(user.id, user.email)}
                      className="flex-1 bg-red-600 hover:bg-red-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-sm font-medium"
                    >
                      <Trash2 size={16} />
                      Delete
                    </button>
                  </div>
                  {/* ✅ NEU: Mobile Password Reset & Verification */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePasswordReset(user.email)}
                      className="flex-1 bg-orange-600 hover:bg-orange-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-xs font-medium"
                    >
                      <Key size={14} />
                      Reset Password
                    </button>
                    {!user.email_verified && (
                      <button
                        onClick={() => handleResendVerification(user.email)}
                        className="flex-1 bg-purple-600 hover:bg-purple-700 text-white px-3 py-2 rounded-lg flex items-center justify-center gap-2 text-xs font-medium"
                      >
                        <Mail size={14} />
                        Verify Email
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-3 md:p-4">
        <div className="flex items-start gap-2 md:gap-3">
          <AlertCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="text-xs md:text-sm text-blue-800 dark:text-blue-200">
            <p className="font-medium mb-1">User Management Features</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Edit all user details including contact info and address</li>
              <li>Select multiple users for bulk role changes or deletion</li>
              <li>Send password reset emails to users</li>
              <li>Resend verification emails to unverified users</li>
              <li>Filter by role and organization</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Edit Modal */}
      {editingUser && (
        <EditUserModal
          user={users.find(u => u.id === editingUser)}
          onClose={() => setEditingUser(null)}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default SuperAdminUsers;